import { GameStateManager } from './StateManager';
import { GameEventSystem } from './EventSystem';
import { EventResult, GameConfig, GameStatus, EndingType, Event, PossibleOutcome } from './types';
import { GameEngine } from './types';
import { Player } from './Player';
import { SpecialEnding } from './types';

// 定义GameData接口
interface GameData {
  player: any;  // 可以根据实际情况定义更详细的类型
  currentEvent: Event | null;
  gameStatus: GameStatus;
}



/**
 * 默认游戏配置
 */
const DEFAULT_CONFIG: GameConfig = {
  startTime: 480, // 8:00 AM (in minutes)
  workStartTime: 540, // 9:00 AM (in minutes)
  totalDistance: 20, // 20 kilometers
  minEventsCount: 10,
  maxEventsCount: 20
};

/**
 * 游戏核心类
 * 负责协调各个系统，管理游戏流程
 */
export class GameCore implements GameEngine {
  private stateManager: GameStateManager;
  private eventSystem: GameEventSystem;
  private config: GameConfig;
  private eventListeners: Map<string, Function[]> = new Map();
  private player: Player;
  private gameStatus: GameStatus = GameStatus.NOT_STARTED;
  private currentEvent: any = null;
  private distanceChangeFactor: number;

  /**
   * 构造函数
   * @param config 游戏配置（可选）
   */
  constructor(config: Partial<GameConfig> = {}) {
    // 合并默认配置和用户配置
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.distanceChangeFactor = this.config.distanceChangeFactor ?? 1;
    
    // 初始化各系统
    this.stateManager = new GameStateManager(this.config);
    this.eventSystem = new GameEventSystem(this.stateManager);
    this.player = new Player(this.stateManager);
  }

  /**
   * 初始化游戏
   */
  async initialize(): Promise<void> {
    this.triggerEvent('loading-start');
    
    try {
      // 加载事件数据
      await this.eventSystem.loadEvents();
      this.triggerEvent('loading-complete');
    } catch (error) {
      this.triggerEvent('loading-error', error);
    }
  }

  /**
   * 开始游戏
   */
  startGame(): void {
    // 重置游戏状态
    this.stateManager.resetGame();
    this.stateManager.startGame();
    
    // 设置游戏状态为运行中
    this.gameStatus = GameStatus.RUNNING;
    
    // 触发游戏开始事件
    this.triggerEvent('game-started');
    
    // 重置currentEvent
    this.currentEvent = null;
    
    // 直接生成第一个随机事件
    this.generateNextEvent();
  }

  /**
   * 开始新游戏
   */
  startNewGame(): void {
    // 调用统一的startGame方法
    this.startGame();
  }

  /**
   * 处理玩家选择
   * @param choiceIndex 选择索引
   * @returns 选择结果
   */
  processChoice(choiceIndex: number): EventResult | undefined {
    if (!this.currentEvent || choiceIndex < 0 || choiceIndex >= this.currentEvent.choices.length) {
      this.generateNextEvent(); 
      return undefined;
    }

    const choice = this.currentEvent.choices[choiceIndex];
    this.stateManager.addSeenEvent(this.currentEvent.id);

    // Initialize with values from the choice object
    let timeChangeFromSource: number | undefined = choice.timeChange;
    let distanceChangeFromSource: number | undefined = choice.distanceChange;
    let actualResultMessage = choice.resultMessage || "你做出了选择。";
    let specialEffect = choice.specialEffect;
    const nextEventId = choice.nextEventId;
    const nextEventDelay = choice.nextEventDelay;

    if (choice.possibleOutcomes && choice.possibleOutcomes.length > 0) {
      const outcome = this.calculateOutcome(choice.possibleOutcomes);
      if (outcome) {
        actualResultMessage = outcome.resultMessage || actualResultMessage;
        specialEffect = outcome.specialEffect ?? specialEffect;

        if (typeof outcome.minTimeChange === 'number' && typeof outcome.maxTimeChange === 'number') {
          timeChangeFromSource = this.getRandomValue(outcome.minTimeChange, outcome.maxTimeChange);
        }
        if (typeof outcome.minDistanceChange === 'number' && typeof outcome.maxDistanceChange === 'number') {
          distanceChangeFromSource = this.getRandomValue(outcome.minDistanceChange, outcome.maxDistanceChange);
        }
      }
    }

    const finalTimeChange = Number(timeChangeFromSource) || 0;
    const finalDistanceChange = Number(distanceChangeFromSource) || 0;
    const factor = this.distanceChangeFactor;
    const distanceChangeWithFactor = finalDistanceChange * factor;

    this.stateManager.updateTime(finalTimeChange);
    this.stateManager.updateDistance(distanceChangeWithFactor);

    if (specialEffect) {
      this.stateManager.addSpecialEffect(specialEffect);
    }

    const eventResultForEmit: EventResult = {
      timeChange: finalTimeChange,
      distanceChange: distanceChangeWithFactor,
      resultMessage: actualResultMessage,
      specialEffect: specialEffect,
    };
    this.triggerEvent('choice-made', eventResultForEmit);

    if (this.stateManager.getGameStatus() === GameStatus.GAME_OVER) {
      return eventResultForEmit; 
    }

    if (nextEventId) {
      if (typeof nextEventDelay === 'number' && nextEventDelay > 0) {
        this.stateManager.addDelayedEvent({ eventId: nextEventId, delay: nextEventDelay });
        this.generateNextEvent();
      } else {
        this.generateSpecificEvent(nextEventId);
      }
    } else {
      this.generateNextEvent();
    }

    return eventResultForEmit;
  }

  /**
   * 触发下一个事件
   * @returns 是否成功触发事件
   */
  triggerNextEvent(): boolean {
    // 更新延迟事件计数
    this.stateManager.updateDelayedEvents();
    
    // 检查是否有准备好的延迟事件
    const delayedEvent = this.stateManager.getNextDelayedEvent();
    if (delayedEvent) {
      this.generateSpecificEvent(delayedEvent.eventId);
      return true;
    }
    
    // 生成新的随机事件
    this.generateNextEvent();
    return true;
  }

  /**
   * 生成下一个随机事件
   */
  private generateNextEvent(): void {
    const state = this.stateManager.getState();
    const event = this.eventSystem.getRandomEvent(state);
    
    if (!event) {
      this.endGame(); 
      return;
    }

    this.currentEvent = event;
    this.stateManager.setCurrentEvent(event);
    this.stateManager.addSeenEvent(event.id);
    this.triggerEvent('event-generated', event);
  }

  /**
   * 生成特定事件
   * @param eventId 事件ID
   */
  private generateSpecificEvent(eventId: string): void {
    const event = this.eventSystem.getEventById(eventId);
    
    if (!event) {
      this.generateNextEvent();
      return;
    }

    this.currentEvent = event;
    this.stateManager.setCurrentEvent(event);
    this.stateManager.addSeenEvent(event.id);
    this.triggerEvent('event-generated', event);
  }

  /**
   * 结束游戏
   */
  public endGame(): void {
    const state = this.stateManager.getState();
    const endingType = this.stateManager.getEndingType();
    
    let specialEnding: SpecialEnding | null = null;
    if (endingType === EndingType.SPECIAL) {
      specialEnding = this.stateManager.getSpecialEnding();
    }
    
    this.triggerEvent('game-over', {
      state,
      endingType,
      specialEndingTitle: specialEnding?.title,
      specialEndingDescription: specialEnding?.description
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState() {
    return this.stateManager.getState();
  }

  /**
   * 事件监听
   * @param eventName 事件名称
   * @param callback 回调函数
   */
  addEventListener(eventName: string, callback: Function): void {
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, []);
    }
    
    this.eventListeners.get(eventName)?.push(callback);
  }

  /**
   * 移除事件监听
   * @param eventName 事件名称
   * @param callback 回调函数
   */
  removeEventListener(eventName: string, callback: Function): void {
    if (!this.eventListeners.has(eventName)) {
      return;
    }
    
    const listeners = this.eventListeners.get(eventName) || [];
    const index = listeners.indexOf(callback);
    
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  }

  /**
   * 触发事件
   * @param eventName 事件名称
   * @param data 事件数据
   */
  private triggerEvent(eventName: string, data?: any): void {
    if (!this.eventListeners.has(eventName)) {
      return;
    }
    
    const listeners = this.eventListeners.get(eventName) || [];
    for (const listener of listeners) {
      try {
        listener(data);
      } catch (error) {
        // 事件监听器错误处理
      }
    }
  }

  /**
   * 获取游戏数据
   * @returns 游戏数据
   */
  public getGameData(): GameData {
    return {
      player: this.player.getState(),
      currentEvent: this.currentEvent,
      gameStatus: this.gameStatus
    };
  }

  /**
   * 获取游戏状态
   * @returns 游戏状态
   */
  public getGameStatus(): GameStatus {
    return this.gameStatus;
  }

  /**
   * 获取特殊结局
   * @returns 特殊结局
   */
  public getSpecialEnding(): SpecialEnding | null {
    // 使用状态管理器中的getSpecialEnding方法
    return this.stateManager.getSpecialEnding();
  }

  /**
   * 重置游戏
   */
  public resetGame(): void {
    this.gameStatus = GameStatus.NOT_STARTED;
    this.stateManager.resetGame();
    this.currentEvent = null;
  }

  private getRandomValue(min: number, max: number): number {
    return this.getRandomInRange(min, max);
  }

  private calculateOutcome(outcomes: PossibleOutcome[]): PossibleOutcome | null {
    if (!outcomes || outcomes.length === 0) {
      return null;
    }
    const totalWeight = outcomes.reduce((sum, outcome) => sum + (outcome.weight || 1), 0);
    if (totalWeight === 0) { // Fallback: if all weights are 0, pick one randomly
      return outcomes[Math.floor(Math.random() * outcomes.length)];
    }
    let randomNum = Math.random() * totalWeight;
    for (const outcome of outcomes) {
      const weight = outcome.weight || 1;
      if (randomNum < weight) {
        return outcome;
      }
      randomNum -= weight;
    }
    return null; // Should not happen if totalWeight > 0 and outcomes is not empty
  }

  private getRandomInRange(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    if (min > max) {
        [min, max] = [max, min]; // 如果min > max，则交换它们
    }
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  // Potentially other methods like getRandomFloatInRange might be here or need to be restored if also removed.
} 